import React, { useMemo, useState } from 'react';
import {
  Button,
  TextInput,
  Tag,
  InlineNotification,
} from '@carbon/react';
import { Add, Star, StarFilled, TrashCan } from '@carbon/react/icons';

import {
  useGetVariantGroupsQuery,
  useCreateVariantGroupMutation,
  useAddVariantMutation,
  useUpdateVariantMutation,
  useRemoveVariantMutation,
  useSetVariantGroupDefaultMutation,
} from '@/redux/new_services/variantGroups';

const ORIENTATION_LABEL = {
  0: 'Quer',
  1: 'Hoch',
  2: 'Multi',
};

// findGroupContainingMedia walks every variant group and returns the first
// one where any variant references this media id (either as landscape or
// portrait slot). Used to auto-detect that the currently edited element
// already lives inside a variant group.
function findGroupContainingMedia(groups, mediaId) {
  for (const g of groups) {
    for (const v of g.variants || []) {
      if (v.landscape_media_id === mediaId || v.portrait_media_id === mediaId) {
        return g;
      }
    }
  }
  return null;
}

// firstVariantSlotsFromMedia builds the {landscape_media_id, portrait_media_id}
// payload for the very first variant of a freshly created group, populating
// the slot(s) that the element being edited (and optionally its pair partner)
// already cover.
function firstVariantSlotsFromMedia(item) {
  const slots = { landscape_media_id: null, portrait_media_id: null };
  if (!item) return slots;
  const partner = item._pair_partner || null;

  if (item.orientation === 0) slots.landscape_media_id = item.model.ID;
  if (item.orientation === 1) slots.portrait_media_id = item.model.ID;

  if (partner) {
    if (partner.orientation === 0 && !slots.landscape_media_id) {
      slots.landscape_media_id = partner.model.ID;
    }
    if (partner.orientation === 1 && !slots.portrait_media_id) {
      slots.portrait_media_id = partner.model.ID;
    }
  }
  return slots;
}

// ElementVariantsAssignment — Tab content. Two states:
//
//   1. Element is not in any variant group → inline form to spin up a new
//      group with N named variants. The first variant inherits the element
//      (and its pair partner if present) and is marked as the group default.
//
//   2. Element is already in a variant group → list every variant with its
//      name + landscape/portrait media references, set/unset default, add or
//      remove individual variants.
const ElementVariantsAssignment = ({ item, editable = true }) => {
  const { data: groups = [] } = useGetVariantGroupsQuery();
  const [createGroup, { isLoading: isCreating }] = useCreateVariantGroupMutation();
  const [addVariant, { isLoading: isAdding }] = useAddVariantMutation();
  const [updateVariant] = useUpdateVariantMutation();
  const [removeVariant] = useRemoveVariantMutation();
  const [setDefault] = useSetVariantGroupDefaultMutation();

  const myGroup = useMemo(
    () => (item?.model?.ID ? findGroupContainingMedia(groups, item.model.ID) : null),
    [groups, item],
  );

  const [error, setError] = useState(null);

  // Inline-create form state — start empty so the admin only sees variants
  // they actively add. Submit validation requires at least one entry.
  const [newGroupName, setNewGroupName] = useState('');
  const [newVariantNames, setNewVariantNames] = useState([]);
  // Add-to-existing form state
  const [newVariantName, setNewVariantName] = useState('');

  if (!item?.model?.ID) {
    return (
      <InlineNotification
        kind="info"
        title="Hinweis"
        subtitle="Speichere das Element, bevor du Varianten anlegst."
        hideCloseButton
        lowContrast
      />
    );
  }

  if (!myGroup) {
    // Inline-create-mode: brand new group, current element fills the first
    // variant's slots.
    const handleCreate = async () => {
      const name = newGroupName.trim();
      const cleanNames = newVariantNames.map((n) => n.trim()).filter(Boolean);
      if (!name) {
        setError('Gruppen-Name fehlt');
        return;
      }
      if (cleanNames.length === 0) {
        setError('Mindestens eine Variante eintragen');
        return;
      }
      try {
        const firstSlots = firstVariantSlotsFromMedia(item);
        const variants = cleanNames.map((vname, i) => i === 0
          ? { name: vname, ...firstSlots }
          : { name: vname, landscape_media_id: null, portrait_media_id: null },
        );
        await createGroup({
          name,
          description: '',
          variants,
        }).unwrap();
        setError(null);
        setNewGroupName('');
        setNewVariantNames([]);
      } catch (e) {
        setError('Anlegen fehlgeschlagen');
      }
    };

    const updateName = (idx, value) => {
      setNewVariantNames((cur) => cur.map((n, i) => (i === idx ? value : n)));
    };
    const removeName = (idx) => {
      setNewVariantNames((cur) => cur.filter((_, i) => i !== idx));
    };
    const addName = () => {
      setNewVariantNames((cur) => [...cur, '']);
    };

    return (
      <div className="element-variants-assignment">
        <p className="element-variants-assignment__lead">
          Lege eine Varianten-Gruppe für dieses Element an. Dieses Element
          {item._pair_partner ? ' (inkl. Hoch+Quer-Paar) ' : ' '}
          wird automatisch die erste Variante und der Standard. Den Inhalt
          der weiteren Varianten kannst du später zuweisen.
        </p>

        {error && (
          <InlineNotification
            kind="error"
            title="Fehler"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
            lowContrast
          />
        )}

        <TextInput
          id="variant-group-new-name"
          labelText="Gruppen-Name"
          placeholder="z. B. Aktion Mai"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          disabled={!editable}
        />

        <h5>Varianten</h5>
        {newVariantNames.length === 0 ? (
          <p className="element-variants-assignment__empty">
            Noch keine Varianten. Füge mindestens eine hinzu — die erste wird
            mit diesem Element verknüpft und als Standard markiert.
          </p>
        ) : (
          <ul className="element-variants-assignment__list">
            {newVariantNames.map((n, idx) => (
              <li key={idx}>
                <TextInput
                  id={`new-variant-${idx}`}
                  labelText={idx === 0 ? 'Variante 1 (Standard, dieses Element)' : `Variante ${idx + 1}`}
                  value={n}
                  onChange={(e) => updateName(idx, e.target.value)}
                  disabled={!editable}
                />
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={TrashCan}
                  iconDescription="Entfernen"
                  onClick={() => removeName(idx)}
                  disabled={!editable}
                />
              </li>
            ))}
          </ul>
        )}
        <div className="element-variants-assignment__actions">
          <Button kind="ghost" size="sm" renderIcon={Add} onClick={addName} disabled={!editable}>
            Variante hinzufügen
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!editable || isCreating || newVariantNames.length === 0 || !newGroupName.trim()}
          >
            Gruppe anlegen
          </Button>
        </div>
      </div>
    );
  }

  // Element is already part of a group — show the manage view.
  const variants = myGroup.variants || [];
  const isDefaultVariantId = myGroup.default_variant_id || null;

  const handleAddVariant = async () => {
    const name = newVariantName.trim();
    if (!name) return;
    try {
      await addVariant({ id: myGroup.model.ID, name }).unwrap();
      setNewVariantName('');
    } catch (e) {
      setError('Variante konnte nicht hinzugefügt werden');
    }
  };

  const handleRenameVariant = async (variant, name) => {
    if (name === variant.name) return;
    try {
      await updateVariant({
        id: variant.model.ID,
        groupId: myGroup.model.ID,
        name,
        position: variant.position,
        landscapeMediaId: variant.landscape_media_id,
        portraitMediaId: variant.portrait_media_id,
      }).unwrap();
    } catch (e) {
      setError('Umbenennen fehlgeschlagen');
    }
  };

  const handleRemoveVariant = async (variant) => {
    if (!confirm(`Variante "${variant.name}" wirklich löschen?`)) return;
    try {
      await removeVariant({ id: variant.model.ID, groupId: myGroup.model.ID }).unwrap();
    } catch (e) {
      setError('Löschen fehlgeschlagen');
    }
  };

  const handleSetDefault = async (variant) => {
    try {
      await setDefault({ id: myGroup.model.ID, variantId: variant.model.ID }).unwrap();
    } catch (e) {
      setError('Default-Markierung fehlgeschlagen');
    }
  };

  return (
    <div className="element-variants-assignment">
      <p className="element-variants-assignment__lead">
        Dieses Element ist Teil der Varianten-Gruppe <strong>{myGroup.name}</strong>.
        Die ausführliche Slot-Verwaltung (Hoch+Quer pro Variante) findest du
        in der Admin-Übersicht unter „Varianten-Gruppen".
      </p>

      {error && (
        <InlineNotification
          kind="error"
          title="Fehler"
          subtitle={error}
          onCloseButtonClick={() => setError(null)}
          lowContrast
        />
      )}

      <ul className="element-variants-assignment__variants">
        {variants.map((v) => {
          const isDefault = v.model.ID === isDefaultVariantId;
          const lm = v.landscape_media;
          const pm = v.portrait_media;
          return (
            <li key={v.model.ID} className={isDefault ? 'is-default' : ''}>
              <div className="element-variants-assignment__variant-name">
                <TextInput
                  id={`variant-name-${v.model.ID}`}
                  labelText="Name der Variante"
                  hideLabel
                  defaultValue={v.name}
                  onBlur={(e) => handleRenameVariant(v, e.target.value.trim())}
                  disabled={!editable}
                />
              </div>
              <div className="element-variants-assignment__slots">
                {lm ? <Tag type="cool-gray">Quer: {lm.name}</Tag> : <Tag type="warm-gray">Quer: —</Tag>}
                {pm ? <Tag type="cool-gray">Hoch: {pm.name}</Tag> : <Tag type="warm-gray">Hoch: —</Tag>}
              </div>
              <div className="element-variants-assignment__variant-actions">
                {isDefault ? (
                  <Tag type="green" renderIcon={StarFilled}>Standard</Tag>
                ) : (
                  <Button kind="ghost" size="sm" renderIcon={Star} onClick={() => handleSetDefault(v)} disabled={!editable}>
                    Als Standard
                  </Button>
                )}
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={TrashCan}
                  iconDescription="Variante entfernen"
                  onClick={() => handleRemoveVariant(v)}
                  disabled={!editable}
                />
              </div>
            </li>
          );
        })}
      </ul>

      <div className="element-variants-assignment__add">
        <TextInput
          id="variant-group-new-variant-name"
          labelText="Neue Variante hinzufügen"
          placeholder="z. B. 3 Euro"
          value={newVariantName}
          onChange={(e) => setNewVariantName(e.target.value)}
          disabled={!editable}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddVariant();
            }
          }}
        />
        <Button
          kind="secondary"
          renderIcon={Add}
          onClick={handleAddVariant}
          disabled={!editable || !newVariantName.trim() || isAdding}
        >
          Hinzufügen
        </Button>
      </div>
    </div>
  );
};

export default ElementVariantsAssignment;
